import { inject } from '@angular/core';
import { Page } from '@rx-lib/ui';
import { RestService, RouteService } from '@rx-lib/data';
import { rxState } from '@rx-angular/state';
import { map, tap, combineLatestWith, filter, take } from 'rxjs/operators';
import { appRoutes } from './app.routes';
import { environment } from './config/environment';
import { AppState, RouteMapEntry } from '@rx-lib/models';


export const state = () => {

	const restService = inject(RestService);
  const routeService = inject(RouteService);

	const state = rxState<AppState>(({ set, connect }) => {

		/* SET ROUTES */
    
    set({
    	routes: appRoutes
    		.filter((route) => !!route.data)
					.map((route) => ({
						...route,
						path: route.data ? route.data['url'] : ''
					}))
    });
    
    
    /* URL */

    connect('url', routeService.url$);

    
    /* PAGES, PROJEKTY */

    connect(restService.getAllPages(environment.url).pipe(
    	tap((pages) => console.log('app state  pages', pages)),
      map((pages: Page[]) => ({
				pages,
				projekty: pages.filter((page) => page.type === 'projekt'),
      })),
      take(1)
    ));
	});


	/* ROUTES MAP */

	state.connect('routesMap', state.select('pages').pipe(
		map((pages) => buildFlatRouteMap(pages)),
		tap((routesMap) => console.log('app state routesMap', routesMap)),
	));


	/* NAVIGATION */

	const url$ = state.select('url');

	state.connect('navigation', url$.pipe(
	  combineLatestWith(state.select('routesMap')),
	  map(([url, map]) => {
	    const entry = map.get(url) ?? [...map.values()][0];
	    return {
	    		next: { title: map.get(entry?.nextUrl!)?.page.title ?? '', url: entry?.nextUrl ?? '' },
		      prev: { title: map.get(entry?.prevUrl!)?.page.title ?? '', url: entry?.prevUrl ?? '' }
	    	};
	  }),
	  tap((navigation) => console.log('app state URL$ CHANGE navigation', navigation)),
	));


	/* CURRENT PAGE */

	state.connect('currentRoute', url$.pipe(
	  combineLatestWith(state.select('routesMap')),
	  tap(([url, map]) => console.log('app state URL$ CHANGE [url, map]', [url, map])),
	  map(([url, map]) => map.get(url) ?? [...map.values()][0]),
	  tap((currentRoute) => console.log('app state URL$ CHANGE currentRoute', currentRoute)),
	));

	/* ADD SUBROUTES TO ROUTES */

	state.connect('routes', state.select('routesMap').pipe(
		map(() => state.get('routes')
			.filter((route) => route.data && route.data['url'])
			.map((route) => {
				const subRoutes = route.children && route.data ? state.get(route.data['url']) : false;
				const children = subRoutes ? subRoutes.map((child: Page) => ({
					...child,
					path: child.media.length === 1 ? child.path : child.path + '/1'
				})) : false;
				return {
					...route,
					children
				}
			})
		),
		tap((routes) => console.log('app state routes', routes)),
		take(1)
	));

	return state;
};



/* HELPERS */


const buildFlatRouteMap = (pages: Page[]): Map<string, RouteMapEntry> => {
  const routeMap = new Map<string, RouteMapEntry>();
  const flatUrls: string[] = [];

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];

    for (let j = 0; j < page.media.length; j++) {
      const url = page.media.length === 1 && page.type !== 'projekt' ? page.path : `${page.path}/${j + 1}`;
      flatUrls.push(url);
      routeMap.set(url, {
        url,
        page,
        index: 0, // set later
        subIndex: j,
        nextUrl: '', // set later
        prevUrl: '' // set later
      });
    }
  }

  // Back-link next/prev URLs now that flatUrls are known
  flatUrls.forEach((url, i) => {
    const entry = routeMap.get(url);
    if (!entry) return;

    entry.index = i;
    entry.nextUrl = routeMap.get(flatUrls[i + 1])?.url || routeMap.get(flatUrls[0])!.url;
    entry.prevUrl = routeMap.get(flatUrls[i - 1])?.url || routeMap.get(flatUrls[flatUrls.length - 1])!.url;
  });

  return routeMap;
};

